import {
  Avatar,
  Box,
  Center,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger, Spacer, Text
} from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { AiOutlineAudit, AiTwotoneContainer } from 'react-icons/ai'
import { BiSupport } from 'react-icons/bi'
import { BsFillShareFill } from 'react-icons/bs'
import { CgProfile } from 'react-icons/cg'
import { FaFileInvoiceDollar } from 'react-icons/fa'
import { HiUserGroup } from 'react-icons/hi'
import { IoLogOutOutline } from 'react-icons/io5'
import { RiFileList3Fill } from 'react-icons/ri'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'
import ProfileDropDown from '../../../graphics/navbar/ProfileDropDown'
import { colors } from '../../../ui-kit'
import type { UserSessionResponse } from '../../interfaces'

const NavGroupWrapper = styled.ul`
  width: 100%;
  list-style-type: none;
  border: none;
  object-fit: contain;
`

const NavItemWrapper = styled.li`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  color: ${colors.appSlate};
  font-weight: 500;
  border-bottom: 1px solid ${colors.appGreyFour};

  .navLink {
    padding: 0.375em 0.75em 0.375em 0.75em;

    svg {
      vertical-align: middle;
    }

    :hover {
      cursor: pointer;
      background-color: ${colors.appPaleGreyTwo};
    }
  }

  :last-child {
    border-bottom: none;
  }
`
const NavBillingGroupWrapper = styled.ul`
  width: 100%;
  list-style-type: none;
  border: none;
  object-fit: contain;
  padding: 0.375em 0 0.375em 0;
  border-bottom: 1px solid ${colors.appGreyFour};
`

const NavBillingItemWrapper = styled.li`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  color: ${colors.appSlate};
  font-weight: 500;

  .navLink {
    padding: 0.375em 0.75em 0.375em 0.75em;

    svg {
      vertical-align: middle;
    }

    :hover {
      cursor: pointer;
      background-color: ${colors.appPaleGreyTwo};
    }
  }
`

const NavLinkTextWrapper = styled.span`
  padding-left: 0.5em;
`

const NavProfileBox = styled(Box) <{ open: boolean }>`
  margin: 1em 0 1em 0;
  cursor: pointer;
  padding: 0.5em;
  display: flex;
  font-weight: 500;
  align-items: center;
`

const StyledCenter = styled(Center) <{ open: boolean }>`
  padding: 4px;
  border-radius: 6px;
  height: 24px;
  width: 24px;
  background-color: #4A5568CC;
`

const NavProfileText = styled.span`
  line-height: var(--chakra-sizes-8);
  margin-left: var(--chakra-sizes-2);
`

interface Props {
  isOpen: boolean
  user: UserSessionResponse | null
}

const NavBarProfile = ({ isOpen, user }: Props): React.ReactElement => {
  const { t } = useTranslation()

  const billingRoutes = [
    {
      to: '/referrals',
      icon: { label: 'Referrals', icon: BsFillShareFill },
      label: t('referrals_and_coupons', 'Referrals and coupons'),
      viewCondition:
        user?.accountType === 'normal' &&
        user.permissions.includes('SELF_REFERRAL_VIEW'),
    },
    {
      to: '/user-management',
      icon: { label: 'User Management', icon: HiUserGroup },
      label: t('user_management', 'User Management'),
      viewCondition: user?.permissions.includes('SELF_TEAM_VIEW'),
    },
    {
      to: '/billing',
      icon: { label: 'Billing Center', icon: RiFileList3Fill },
      label: t('billing_center', 'Billing Center'),
      viewCondition:
        user?.pricingPlan.isPaidPlan &&
        user.permissions.includes('SELF_BILLING_VIEW'),
    },
    {
      to: '/invoices',
      icon: { label: 'Invoices', icon: FaFileInvoiceDollar },
      label: t('invoices', 'Invoices'),
      viewCondition: user?.permissions.includes('SELF_BILLING_VIEW'),
    },
    {
      to: '/audit-logs',
      icon: { label: 'Audit logs', icon: AiOutlineAudit },
      label: t('audit_logs', 'Audit logs'),
      viewCondition: user?.permissions.includes('SELF_TEAM_VIEW'),
    },
    {
      to: '/dam-audit-logs',
      icon: { label: 'DAM audit logs', icon: AiTwotoneContainer },
      label: t('dam_audit_logs', 'DAM audit logs'),
      viewCondition: user?.permissions.includes('SELF_TEAM_VIEW'),
    },
    {
      to: '/support',
      icon: { label: 'Support', icon: BiSupport },
      label: t('support', 'Support'),
      viewCondition: true,
    },
  ]

  return (
    <Popover placement="right-start" arrowShadowColor="none" trigger="click">
      <PopoverTrigger>
        <NavProfileBox open={isOpen}>
          <Avatar size="sm" name={user?.userName} />
          {isOpen ? (
            <>
              <div>
                <Text as="div" isTruncated marginX="0.75em" maxWidth={"150px"}>
                  {user?.userName}
                </Text>
                <Text as="div" isTruncated marginX="0.75em" color="#CDD5DF" fontSize="12px" maxWidth={"150px"}>
                  {user?.userEmail}
                </Text>
              </div>

              <Spacer />
              <StyledCenter cursor="pointer">
                <Icon as={ProfileDropDown} boxSize={3} />
              </StyledCenter>
            </>
          ) : null}
        </NavProfileBox>
      </PopoverTrigger>
      <PopoverContent maxWidth="16em">
        <PopoverArrow bgColor="appWhite" />
        <PopoverBody
          padding={0}
          border="none"
          borderRadius="4px"
          boxShadow="1px 1px 10px 1px rgba(0,0,0, 0.1)"
        >
          <NavGroupWrapper>
            <NavItemWrapper>
              <NavLink exact to="/profile" className="navLink">
                <Icon aria-label="Profile icon" as={CgProfile} boxSize="1em" />
                <NavLinkTextWrapper>
                  {t('profile', 'Profile')}
                </NavLinkTextWrapper>
              </NavLink>
            </NavItemWrapper>
            <NavBillingGroupWrapper>
              {billingRoutes
                .filter(x => x.viewCondition)
                .map(billingRoute => (
                  <NavBillingItemWrapper key={billingRoute.label}>
                    <NavLink
                      exact
                      key={`nav-item-${billingRoute.label}`}
                      className="navLink"
                      to={billingRoute.to}
                    >
                      <Icon
                        aria-label={billingRoute.icon.label}
                        as={billingRoute.icon.icon}
                        boxSize="1em"
                      />
                      <NavLinkTextWrapper>
                        {billingRoute.label}
                      </NavLinkTextWrapper>
                    </NavLink>
                  </NavBillingItemWrapper>
                ))}
            </NavBillingGroupWrapper>
            <NavItemWrapper>
              <a className="navLink" href="/logout">
                <Icon
                  aria-label="Log out icon"
                  as={IoLogOutOutline}
                  boxSize="1em"
                />
                <NavLinkTextWrapper>
                  {t('logout', 'Log out')}
                </NavLinkTextWrapper>
              </a>
            </NavItemWrapper>
          </NavGroupWrapper>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

export default NavBarProfile
